import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FlaskConical, CheckCircle2, XCircle, MinusCircle, Clock, ArrowLeft, ExternalLink } from 'lucide-react';
import { fitnessApi, type RunReport } from '@/services/fitnessApi';

function StatusBadge({ passed, failed, total }: { passed: number; failed: number; total: number }) {
  if (failed > 0) return <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300">{failed} failed</span>;
  if (total > 0 && passed === total) return <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">All passed</span>;
  return <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">{passed}/{total} passed</span>;
}

export default function TestReportsPage() {
  const [runs, setRuns] = useState<RunReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fitnessApi
      .getResults()
      .then((data) => setRuns(data.runs))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex h-64 items-center justify-center text-sm text-slate-500">Loading reports...</div>;
  }

  return (
    <div className="p-8">
      <Link to="/verify" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Verify
      </Link>

      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Test Reports</h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          The latest CI run of every connected repository, as reported by GitHub.
        </p>
      </div>

      {error || runs.length === 0 ? (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 text-center">
          <FlaskConical className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-700" />
          <h2 className="mt-4 font-semibold text-slate-900 dark:text-slate-100">
            {error ? 'Could not load test reports' : 'No CI runs ingested yet'}
          </h2>
          <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
            Runs appear here as GitHub reports them for the repositories this workspace has connected.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {runs.map((run) => (
            <div
              key={run.service}
              className="flex flex-col rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-2">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{run.workflow}</h2>
                <StatusBadge passed={run.summary.passed} failed={run.summary.failed} total={run.summary.total} />
              </div>
              <p className="mt-0.5 text-xs text-slate-500 font-mono">{run.repo}</p>
              <div className="mt-3 flex-1 space-y-1.5">
                {run.results.slice(0, 5).map((r) => (
                  <div key={r.name} className="flex items-center gap-2 text-sm">
                    {r.status === 'passed' ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                    ) : r.status === 'failed' ? (
                      <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                    ) : (
                      <MinusCircle className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    )}
                    <span className="truncate text-slate-600 dark:text-slate-400">{r.name}</span>
                  </div>
                ))}
                {run.results.length > 5 && (
                  <p className="text-xs text-slate-400 pl-5">+{run.results.length - 5} more jobs</p>
                )}
                {run.results.length === 0 && (
                  <p className="text-xs text-slate-400">No per-job results reported for this run.</p>
                )}
              </div>
              <div className="mt-4 flex items-center justify-between text-xs text-slate-500 border-t border-slate-200 dark:border-slate-800 pt-3">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {run.timestamp
                    ? new Date(run.timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                    : '—'}
                </span>
                {run.runUrl ? (
                  <a
                    href={run.runUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-mono hover:text-slate-700 dark:hover:text-slate-300"
                  >
                    {run.commit?.slice(0, 7)}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <code className="font-mono">{run.commit?.slice(0, 7)}</code>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
